import React, { useEffect } from "react";
import { useTitle } from "../provider/TitleProvider";

const Privacy = () => {
  const { setTitle } = useTitle();

  useEffect(() => {
    setTitle("Privacy Policy");
  }, []);

  return (
    <div className="auto-container">
      <div className="row clearfix text-foreground">
        <p>
          This Privacy Policy (<strong>Policy</strong>) describes how Retreevo, Inc. (<strong>Retreevo</strong>,{" "}
          <strong>we</strong>, <strong>us</strong> or <strong>our</strong>) collects, uses, discloses, and protects
          personal information in connection with the Retreevo platform, websites, and related services (collectively,
          the <strong>Services</strong>). By using the Services, you agree to the collection and use of information as
          described in this Policy.
        </p>
        <p>
          Retreevo provides a calling, messaging, and customer relationship management platform for real estate
          professionals. This Policy applies both to information about our customers and account users, and to
          information our customers process about their own contacts through the Services.
        </p>

        <h5>
          <strong>1. Information We Collect</strong>
        </h5>
        <p>
          <i>
            <strong>Information you provide:</strong>
          </i>{" "}
          When you register, subscribe, or communicate with us, we collect information such as your name, email address,
          phone number, organization details, billing information, and any content you submit through the Services.
        </p>
        <p>
          <i>
            <strong>Contact and campaign data:</strong>
          </i>{" "}
          Our customers upload and generate data about their own leads and contacts, including names, phone numbers,
          email addresses, property details, call recordings and transcripts, message history, dispositions, and
          consent and opt-out status. We process this data on our customers' behalf in order to provide the Services.
        </p>
        <p>
          <i>
            <strong>Usage and device information:</strong>
          </i>{" "}
          We automatically collect technical data such as IP address, browser type, device identifiers, pages viewed,
          and feature usage, including through cookies and similar technologies.
        </p>
        <p>
          <i>
            <strong>Information from third parties:</strong>
          </i>{" "}
          We may receive information from integration partners, telecommunications and payment providers, and data
          providers that you connect to or that help us deliver the Services.
        </p>

        <h5>
          <strong>2. How We Use Information</strong>
        </h5>
        <ul>
          <li>to provide, operate, maintain, and improve the Services;</li>
          <li>
            to enable calling, messaging, contact management, automation, and AI calling features you choose to use;
          </li>
          <li>to process transactions, manage billing, and prevent fraud;</li>
          <li>to provide customer support and respond to your requests;</li>
          <li>
            to send service-related communications, and, where permitted, marketing communications about features and
            offers;
          </li>
          <li>to monitor usage, analyze trends, and develop new features; and</li>
          <li>to comply with legal obligations and enforce our terms.</li>
        </ul>

        <h5>
          <strong>3. Calling and Messaging (SMS) Consent</strong>
        </h5>
        <p>
          The Services include calling and SMS messaging features. Where we communicate with you directly (for example,
          account, verification, or support messages), we use your phone number for those purposes; message and data
          rates may apply, and you can opt out of non-essential messages by replying STOP or contacting us.
        </p>
        <p>
          When our customers use the Services to contact their own leads, the customer (not Retreevo) is the party
          responsible for obtaining any consent required to place those calls or send those messages and for honoring
          opt-out requests. Information shared with us for the purpose of supporting A2P 10DLC registration and message
          delivery is used only to enable and maintain messaging on our customers' behalf, and consent obtained for
          messaging is not shared with third parties for their own marketing.
        </p>

        <h5>
          <strong>4. Cookies and Tracking</strong>
        </h5>
        <p>
          We use cookies and similar technologies to keep you signed in, remember your preferences, secure the Services,
          and understand usage. You can control cookies through your browser settings, but disabling them may affect how
          the Services function.
        </p>

        <h5>
          <strong>5. How We Share Information</strong>
        </h5>
        <p>We may share information in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service providers:</strong> with telecommunications carriers, payment processors, hosting and
            infrastructure providers, and other vendors who process information on our behalf to deliver the Services;
          </li>
          <li>
            <strong>Integrations you enable:</strong> with third-party platforms you choose to connect, such as CRM and
            data providers, at your direction;
          </li>
          <li>
            <strong>Legal and safety:</strong> when required to comply with law, respond to legal process, or protect
            the rights, property, or safety of Retreevo, our users, or others;
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger, acquisition, financing, or sale of assets;
            and
          </li>
          <li>
            <strong>With your consent:</strong> when you otherwise direct us to share your information.
          </li>
        </ul>
        <p>We do not sell personal information in exchange for monetary consideration.</p>

        <h5>
          <strong>6. Data Retention</strong>
        </h5>
        <p>
          We retain personal information for as long as necessary to provide the Services, comply with legal,
          accounting, and reporting obligations, resolve disputes, and enforce our agreements. Customer Data is retained
          according to our customers' instructions and account settings, and is deleted or returned following account
          closure as described in our agreements.
        </p>

        <h5>
          <strong>7. Security</strong>
        </h5>
        <p>
          We use administrative, technical, and physical safeguards, including encryption in transit, to protect
          personal information. No method of transmission or storage is completely secure, however, and we cannot
          guarantee absolute security. If a breach affecting your information occurs, we will take reasonable steps to
          investigate and notify affected individuals as required by applicable law.
        </p>

        <h5>
          <strong>8. Your Privacy Rights</strong>
        </h5>
        <p>
          Depending on where you live, you may have the right to access, correct, delete, or port your personal
          information, and to opt out of certain processing. You can update your account information at any time or
          contact us to exercise these rights. We will respond to verified requests as required by applicable law and
          may need to verify your identity before acting on a request.
        </p>

        <h5>
          <strong>9. California Privacy Rights</strong>
        </h5>
        <p>
          If you are a California resident, the California Consumer Privacy Act, as amended, gives you the right to know
          what personal information we collect, use, and disclose, to request deletion or correction of your personal
          information, and to opt out of the sale or sharing of personal information. We do not sell personal
          information. You will not be discriminated against for exercising these rights. To exercise a right, contact
          us at <a href="mailto:support@retreevo.com">support@retreevo.com</a>.
        </p>

        <h5>
          <strong>10. Children's Privacy</strong>
        </h5>
        <p>
          The Services are intended for business use and are not directed to children under 18. We do not knowingly
          collect personal information from children. If we learn that we have collected such information, we will delete
          it.
        </p>

        <h5>
          <strong>11. Third-Party Links and Services</strong>
        </h5>
        <p>
          The Services may link to or integrate with third-party websites and services that have their own privacy
          practices. We are not responsible for those practices and encourage you to review their policies.
        </p>

        <h5>
          <strong>12. Changes to This Policy</strong>
        </h5>
        <p>
          We may update this Policy from time to time. When we make material changes, we will update the date below and
          may provide additional notice. Your continued use of the Services after changes take effect constitutes
          acceptance of the updated Policy.
        </p>

        <h5>
          <strong>13. Contact Us</strong>
        </h5>
        <p>
          If you have questions about this Policy or how we handle your personal information, contact us at{" "}
          <a href="mailto:support@retreevo.com">support@retreevo.com</a>.
        </p>

        <p>Last updated: June 10, 2026</p>
      </div>
    </div>
  );
};

export default Privacy;
